import React from "react";
import clsx from "clsx";
import { useResponsive } from "@/hooks/useResponsive";

const TYPOGRAPHY_THEME = {
  noticia: {
    mobile: "18px",
    mobileLg: "24px",
    tablet: "30px",
    laptop: "40px",
    desktop: "40px",
  },
  windsong: {
    mobile: "30px",
    mobileLg: "30px",
    tablet: "33px",
    laptop: "44px",
    desktop: "55px",
  },
  montserrat: {
    mobile: "14px",
    mobileLg: "15px",
    tablet: "15px",
    laptop: "16px",
    desktop: "17px",
  },
};

const getFontSize = (
  fontKey: "noticia" | "windsong" | "montserrat",
  responsive: any,
) => {
  const fontTheme = TYPOGRAPHY_THEME[fontKey];
  const { isMobile, isMobileLg, isTablet, isLaptop, isDesktop } = responsive;
  if (isMobile) return fontTheme.mobile;
  if (isMobileLg) return fontTheme.mobileLg;
  if (isTablet) return fontTheme.tablet;
  if (isLaptop) return fontTheme.laptop;
  if (isDesktop) return fontTheme.desktop;
  return fontTheme.desktop;
};

export interface ResponsiveSizes {
  mobile?: string;
  mobileLg?: string;
  tablet?: string;
  laptop?: string;
  desktop?: string;
}

const getResponsiveSize = (sizes: ResponsiveSizes, responsive: any) => {
  const { isMobile, isMobileLg, isTablet, isLaptop, isDesktop } = responsive;
  if (isMobile && sizes.mobile) return sizes.mobile;
  if (isMobileLg && sizes.mobileLg) return sizes.mobileLg;
  if (isTablet && sizes.tablet) return sizes.tablet;
  if (isLaptop && sizes.laptop) return sizes.laptop;
  if (isDesktop && sizes.desktop) return sizes.desktop;
  return (
    sizes.desktop ||
    sizes.laptop ||
    sizes.tablet ||
    sizes.mobileLg ||
    sizes.mobile
  );
};

const parseSizeValue = (val: any) => {
  if (val === undefined || val === null || val === "") return undefined;
  const str = String(val).trim();
  // If already a CSS unit (px, rem, em, %, vh, vw, etc.), return as is
  if (/^[0-9.]+(px|rem|em|%|vh|vw|dvh|lvh|svh)$/.test(str)) {
    return str;
  }
  // If it's a number or string containing only digits, append px
  if (/^[0-9.]+$/.test(str)) {
    return `${str}px`;
  }
  // Tailwind sizes mapped to CSS values
  let key = str;
  if (key.startsWith("text-")) {
    key = key.substring(5);
  }
  const tailwindSizes: Record<string, string> = {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "3.75rem",
    "7xl": "4.5rem",
  };
  if (tailwindSizes[key]) {
    return tailwindSizes[key];
  }
  return str;
};

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
  variant?: string;
  sizes?: ResponsiveSizes;

  // Direct responsive props
  mobile?: string | number;
  mobileLg?: string | number;
  tablet?: string | number;
  laptop?: string | number;
  desktop?: string | number;

  // Standard breakpoints aliases
  sm?: string | number;
  md?: string | number;
  lg?: string | number;
  xl?: string | number;
  "2xl"?: string | number;
}

export interface MontserratTextProps extends TypographyProps {}
export interface WindsongTextProps extends TypographyProps {}
export interface NoticiaTextProps extends TypographyProps {}

// Montserrat (General Text)
export const MontserratText = React.forwardRef<
  HTMLElement,
  MontserratTextProps
>(
  (
    {
      as = "p",
      className,
      children,
      variant,
      sizes,
      mobile,
      mobileLg,
      tablet,
      laptop,
      desktop,
      sm,
      md,
      lg,
      xl,
      "2xl": xx,
      style,
      ...props
    },
    ref,
  ) => {
    const Component = as;
    const responsive = useResponsive();

    const mergedSizes: ResponsiveSizes = {
      mobile: parseSizeValue(mobile) || sizes?.mobile,
      mobileLg: parseSizeValue(mobileLg || sm) || sizes?.mobileLg,
      tablet: parseSizeValue(tablet || md) || sizes?.tablet,
      laptop: parseSizeValue(laptop || lg) || sizes?.laptop,
      desktop: parseSizeValue(desktop || xl || xx) || sizes?.desktop,
    };

    const hasSizeProps =
      mobile !== undefined ||
      mobileLg !== undefined ||
      sm !== undefined ||
      md !== undefined ||
      lg !== undefined ||
      xl !== undefined ||
      xx !== undefined ||
      tablet !== undefined ||
      laptop !== undefined ||
      desktop !== undefined ||
      sizes !== undefined;

    const size = hasSizeProps
      ? getResponsiveSize(mergedSizes, responsive)
      : getFontSize("montserrat", responsive);

    const mergedStyle = {
      ...(size ? { fontSize: size } : {}),
      ...style,
    };

    return (
      <Component
        ref={ref}
        className={clsx("font-montserrat", className)}
        style={mergedStyle}
        {...props}
      >
        {children}
      </Component>
    );
  },
);
MontserratText.displayName = "MontserratText";

// Windsong (Highlight Script Text)
export const WindsongText = React.forwardRef<HTMLElement, WindsongTextProps>(
  (
    {
      as = "span",
      className,
      children,
      variant,
      sizes,
      mobile,
      mobileLg,
      tablet,
      laptop,
      desktop,
      sm,
      md,
      lg,
      xl,
      "2xl": xx,
      style,
      ...props
    },
    ref,
  ) => {
    const Component = as;
    const responsive = useResponsive();

    const mergedSizes: ResponsiveSizes = {
      mobile: parseSizeValue(mobile) || sizes?.mobile,
      mobileLg: parseSizeValue(mobileLg || sm) || sizes?.mobileLg,
      tablet: parseSizeValue(tablet || md) || sizes?.tablet,
      laptop: parseSizeValue(laptop || lg) || sizes?.laptop,
      desktop: parseSizeValue(desktop || xl || xx) || sizes?.desktop,
    };

    const hasSizeProps =
      mobile !== undefined ||
      mobileLg !== undefined ||
      sm !== undefined ||
      md !== undefined ||
      lg !== undefined ||
      xl !== undefined ||
      xx !== undefined ||
      tablet !== undefined ||
      laptop !== undefined ||
      desktop !== undefined ||
      sizes !== undefined;

    const size = hasSizeProps
      ? getResponsiveSize(mergedSizes, responsive)
      : getFontSize("windsong", responsive);

    const mergedStyle = {
      ...(size ? { fontSize: size } : {}),
      ...style,
    };

    return (
      <Component
        ref={ref}
        className={clsx(
          "font-windsong bg-gradient-to-r from-[#DBB653] to-[#FFF0B8] bg-clip-text text-transparent inline-block py-2 px-1 w-auto",
          className,
        )}
        style={mergedStyle}
        {...props}
      >
        {children}
      </Component>
    );
  },
);
WindsongText.displayName = "WindsongText";

// Noticia (Title Text)
export const NoticiaText = React.forwardRef<HTMLElement, NoticiaTextProps>(
  (
    {
      as = "h2",
      className,
      children,
      variant,
      sizes,
      mobile,
      mobileLg,
      tablet,
      laptop,
      desktop,
      sm,
      md,
      lg,
      xl,
      "2xl": xx,
      style,
      ...props
    },
    ref,
  ) => {
    const Component = as;
    const responsive = useResponsive();

    const mergedSizes: ResponsiveSizes = {
      mobile: parseSizeValue(mobile) || sizes?.mobile,
      mobileLg: parseSizeValue(mobileLg || sm) || sizes?.mobileLg,
      tablet: parseSizeValue(tablet || md) || sizes?.tablet,
      laptop: parseSizeValue(laptop || lg) || sizes?.laptop,
      desktop: parseSizeValue(desktop || xl || xx) || sizes?.desktop,
    };

    const hasSizeProps =
      mobile !== undefined ||
      mobileLg !== undefined ||
      sm !== undefined ||
      md !== undefined ||
      lg !== undefined ||
      xl !== undefined ||
      xx !== undefined ||
      tablet !== undefined ||
      laptop !== undefined ||
      desktop !== undefined ||
      sizes !== undefined;

    const size = hasSizeProps
      ? getResponsiveSize(mergedSizes, responsive)
      : getFontSize("noticia", responsive);

    const mergedStyle = {
      ...(size ? { fontSize: size } : {}),
      ...style,
    };

    return (
      <Component
        ref={ref}
        className={clsx("font-noticia uppercase", className)}
        style={mergedStyle}
        {...props}
      >
        {children}
      </Component>
    );
  },
);
NoticiaText.displayName = "NoticiaText";
